import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "../../api";
import { toast } from "react-toastify";
import Can from "../../components/Can";
import ConfirmModal from "../../components/common/ConfirmModal";
import ProjectMemberPicker from "../../components/ProjectMemberPicker";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import EmptyState from "../../components/common/EmptyState";
import { AuthContext } from "../../context/AuthContext";
import { formatDateOrDash, formatDateOrNotScheduled } from "./projectWorkspaceUtils";
import {
  MILESTONE_CATEGORIES,
  TASK_BUCKET_OPTIONS,
  UNSCOPED_PHASE,
  allTasksCompleted,
  filterAndSortMilestones,
  filterMilestoneTasks,
  isActivePlanMilestone,
  isMilestoneInactive,
  isPhaseInactive,
  isTaskOverdue,
  milestoneDisplayStatus,
  summarizeMilestones,
  taskBucket,
} from "./milestoneViewUtils";
import "./ProjectWorkspace.css";

const emptyForm = {
  name: "",
  description: "",
  project_phase_id: "",
  project_module_id: "",
  owner_user_id: "",
  planned_date: "",
  actual_date: "",
  sequence: "",
};

// Add/Edit modal -- POST/PUT /projects/:id/milestones(/:milestoneId). Phase
// and Module are both optional (an "unscoped" milestone applies to the
// whole project, same convention already used by the Timeline tab's
// "Unscoped Milestones" section) -- Module is what the expanded row's
// "Module Tasks" view keys off (see ProjectWorkspaceMilestonesTab's
// getMilestoneTasks() call), backward-compatible: a milestone left without
// a module keeps working via its direct task link. Owner reuses
// ProjectMemberPicker fed from this project's own active members (GET
// /projects/:id/members), exactly like the Board tab's assignee filter
// already does.
function MilestoneFormModal({ show, projectId, phases, modules, members, editing, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (editing) {
      setForm({
        name: editing.name || "",
        description: editing.description || "",
        project_phase_id: editing.project_phase_id ? String(editing.project_phase_id) : "",
        project_module_id: editing.project_module_id ? String(editing.project_module_id) : "",
        owner_user_id: editing.owner_user_id ? String(editing.owner_user_id) : "",
        planned_date: editing.planned_date ? String(editing.planned_date).slice(0, 10) : "",
        actual_date: editing.actual_date ? String(editing.actual_date).slice(0, 10) : "",
        sequence: editing.sequence ?? "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [show, editing]);

  const memberOptions = useMemo(
    () =>
      members.map((m) => ({
        id: m.user_id,
        name: m.user_name,
        employee_code: m.employee_code,
        designation_name: m.designation_name,
        department_name: m.department_name,
      })),
    [members]
  );

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warning("Milestone name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      project_phase_id: form.project_phase_id || null,
      project_module_id: form.project_module_id || null,
      owner_user_id: form.owner_user_id || null,
      planned_date: form.planned_date || null,
      actual_date: form.actual_date || null,
      sequence: form.sequence === "" ? undefined : Number(form.sequence),
    };

    try {
      setSaving(true);
      if (editing) {
        await api.put(`/projects/${projectId}/milestones/${editing.id}`, payload);
        toast.success("Milestone updated");
      } else {
        await api.post(`/projects/${projectId}/milestones`, payload);
        toast.success("Milestone created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save milestone");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{editing ? "Edit Milestone" : "Add Milestone"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-12">
            <Form.Label>Name</Form.Label>
            <Form.Control
              placeholder="e.g. UAT Sign-off"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>
          <div className="col-md-12">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <Form.Label>Phase (optional)</Form.Label>
            <Form.Select value={form.project_phase_id} onChange={(e) => handleChange("project_phase_id", e.target.value)}>
              <option value="">Unscoped (whole project)</option>
              {phases.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-6">
            <Form.Label>Module (optional)</Form.Label>
            <Form.Select value={form.project_module_id} onChange={(e) => handleChange("project_module_id", e.target.value)}>
              <option value="">No module</option>
              {modules.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-6">
            <Form.Label>Owner (optional)</Form.Label>
            <ProjectMemberPicker
              users={memberOptions}
              value={form.owner_user_id}
              onChange={(id) => handleChange("owner_user_id", id)}
              placeholder="Select owner..."
            />
          </div>
          <div className="col-md-4">
            <Form.Label>Planned Date</Form.Label>
            <Form.Control type="date" value={form.planned_date} onChange={(e) => handleChange("planned_date", e.target.value)} />
          </div>
          <div className="col-md-4">
            <Form.Label>Actual Date</Form.Label>
            <Form.Control type="date" value={form.actual_date} onChange={(e) => handleChange("actual_date", e.target.value)} />
          </div>
          <div className="col-md-4">
            <Form.Label>Sequence</Form.Label>
            <Form.Control
              type="number"
              value={form.sequence}
              onChange={(e) => handleChange("sequence", e.target.value)}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</> : editing ? "Update Milestone" : "Create Milestone"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const TASK_PAGE_SIZE = 10;

// Segmented completion bar -- completed / in progress / blocked / pending
// widths are the backend summary's own counts; the accessible value is the
// backend's progress_percent (never recomputed here).
function SegmentedProgress({ summary, label }) {
  const total = summary?.total || 0;
  const pct = summary?.progress_percent;
  const seg = (n) => `${total ? ((n || 0) / total) * 100 : 0}%`;
  return (
    <div
      className="pw-ms-bar"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct ?? 0}
    >
      {total > 0 && (
        <>
          <span className="pw-ms-bar-seg pw-ms-bar-done" style={{ width: seg(summary.completed) }} />
          <span className="pw-ms-bar-seg pw-ms-bar-active" style={{ width: seg(summary.in_progress) }} />
          <span className="pw-ms-bar-seg pw-ms-bar-blocked" style={{ width: seg(summary.blocked) }} />
        </>
      )}
    </div>
  );
}

function MilestoneStatusChip({ display }) {
  return (
    <span className={`pw-ms-status pw-ms-status-${display.tone}`} title={display.title}>
      {display.label}
    </span>
  );
}

// Expanded-row panel -- GET /projects/:id/milestones/:milestoneId/tasks.
// Matching priority is entirely the backend's call (never re-derived here):
// when the milestone carries a project_module_id, this shows link_type
// "MODULE" / label "Module Tasks" -- ALL of that module's real project
// tasks, never an exclusive per-milestone subset. Otherwise it falls back
// to link_type "MILESTONE_DIRECT" / label "Milestone Tasks" (the milestone's
// own direct tasks.project_milestone_id link), or "NONE" when neither
// applies. This panel only ever displays whatever the backend sends --
// never hardcodes which label/module applies.
function MilestoneTasksPanel({ milestone, panelState, onRetry, onRefresh, onTaskClick, currentUserId }) {
  const [view, setView] = useState("all");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const allTasks = useMemo(
    () => (Array.isArray(panelState?.data?.tasks) ? panelState.data.tasks : []),
    [panelState]
  );
  const shownTasks = useMemo(
    () => filterMilestoneTasks(allTasks, { view, status, sort, search }, currentUserId),
    [allTasks, view, status, sort, search, currentUserId]
  );
  const myCount = useMemo(
    () => allTasks.filter((t) => currentUserId != null && t.assignee && Number(t.assignee.id) === Number(currentUserId)).length,
    [allTasks, currentUserId]
  );
  const today = dayjs().format("YYYY-MM-DD");

  // Any change to what's shown starts again from the first page.
  useEffect(() => setPage(1), [view, status, sort, search, allTasks]);

  const panelId = `pw-ms-panel-${milestone.id}`;

  if (!panelState || panelState.status === "loading") {
    return (
      <div className="pw-ms-panel" id={panelId}>
        <LoadingState message="Loading milestone tasks…" />
      </div>
    );
  }

  if (panelState.status === "error") {
    return (
      <div className="pw-ms-panel" id={panelId}>
        <ErrorState
          message={panelState.error || "Could not load milestone tasks."}
          action={<Button size="sm" onClick={onRetry}>Retry</Button>}
        />
      </div>
    );
  }

  const data = panelState.data || {};
  const summary = data.summary || {};
  const tasks = allTasks;
  const label = data.label;
  const module = data.module;

  const pageCount = Math.max(1, Math.ceil(shownTasks.length / TASK_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageTasks = shownTasks.slice((currentPage - 1) * TASK_PAGE_SIZE, currentPage * TASK_PAGE_SIZE);
  const hasProgress = summary.progress_percent !== null && summary.progress_percent !== undefined;

  return (
    <div className="pw-ms-panel" id={panelId}>
      <div className="pw-ms-panel-header">
        <div className="pw-ms-panel-heading">
          <div className="pw-ms-panel-title">{milestone.name}</div>
          <div className="pw-ms-panel-meta">
            {milestone.phase_name && <span>{milestone.phase_name}</span>}
            {module && <span className="pw-ms-panel-module">Module: {module.name}</span>}
            {label && <span className="pw-ms-panel-label">{label}</span>}
          </div>
          {milestone.description && (
            <div className="pw-ms-panel-desc" title={milestone.description}>{milestone.description}</div>
          )}
        </div>
        <Button variant="outline-secondary" size="sm" className="pw-ms-refresh" onClick={onRefresh}>
          <i className="fa-solid fa-rotate" aria-hidden="true"></i> Refresh
        </Button>
      </div>

      {tasks.length === 0 ? (
        <p className="pw-no-tasks-note mb-0">No tasks linked.</p>
      ) : (
        <>
          <div className="pw-ms-panel-stats" aria-label="Milestone task statistics">
            <span className="pw-ms-stat"><strong>{summary.total ?? 0}</strong> Total</span>
            <span className="pw-ms-stat pw-ms-stat-done"><strong>{summary.completed ?? 0}</strong> Completed</span>
            <span className="pw-ms-stat pw-ms-stat-active"><strong>{summary.in_progress ?? 0}</strong> In Progress</span>
            <span className="pw-ms-stat"><strong>{summary.pending ?? 0}</strong> Pending</span>
            <span className="pw-ms-stat pw-ms-stat-blocked"><strong>{summary.blocked ?? 0}</strong> Blocked</span>
          </div>
          {hasProgress && (
            <div className="pw-ms-panel-progress">
              <SegmentedProgress summary={summary} label={`${milestone.name} task completion`} />
              <span className="pw-ms-panel-progress-text">{summary.completed ?? 0} / {summary.total ?? 0} Completed</span>
              <span className="pw-ms-panel-progress-pct">{summary.progress_percent}%</span>
            </div>
          )}

          <div className="pw-ms-task-toolbar">
            <div className="btn-group btn-group-sm" role="group" aria-label="Task view">
              <Button
                variant={view === "mine" ? "primary" : "outline-secondary"}
                aria-pressed={view === "mine"}
                onClick={() => setView("mine")}
              >
                My Tasks ({myCount})
              </Button>
              <Button
                variant={view === "all" ? "primary" : "outline-secondary"}
                aria-pressed={view === "all"}
                onClick={() => setView("all")}
              >
                All Tasks ({tasks.length})
              </Button>
            </div>
            <Form.Select
              size="sm"
              className="pw-ms-task-select"
              aria-label="Filter milestone tasks by status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {TASK_BUCKET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
            <Form.Control
              size="sm"
              type="search"
              className="pw-ms-task-search"
              placeholder="Search title or #task number"
              aria-label="Search milestone tasks"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Form.Select
              size="sm"
              className="pw-ms-task-select"
              aria-label="Sort milestone tasks"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="due">Due date</option>
              <option value="priority">Priority</option>
            </Form.Select>
            <span className="pw-ms-task-count">Showing {shownTasks.length} of {tasks.length}</span>
          </div>

          {shownTasks.length === 0 ? (
            <p className="pw-no-tasks-note mb-0">
              {view === "mine" ? "No tasks in this milestone are assigned to you" : "No tasks match"}
              {search.trim() ? ` "${search.trim()}"` : ""}.
            </p>
          ) : (
            <>
              <div className="pw-ms-task-table-wrap">
                <table className="pw-ms-task-table">
                  <thead>
                    <tr>
                      <th scope="col">Task</th>
                      <th scope="col">Assignee</th>
                      <th scope="col">Status</th>
                      <th scope="col">Priority</th>
                      <th scope="col">Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageTasks.map((t) => {
                      const overdue = isTaskOverdue(t, today);
                      return (
                        <tr key={t.id} className={overdue ? "pw-ms-task-overdue" : undefined} data-bucket={taskBucket(t)}>
                          <td className="pw-ms-task-title-cell">
                            <button
                              type="button"
                              className="pw-task-link pw-ms-task-link"
                              title={`#${t.id} ${t.title || ""}`}
                              onClick={() => onTaskClick(t.id)}
                            >
                              <span className="pw-ms-task-id">#{t.id}</span> {t.title}
                            </button>
                          </td>
                          <td className="pw-ms-task-assignee">{t.assignee?.name || <span className="text-muted">Unassigned</span>}</td>
                          <td>
                            {t.status ? (
                              <span
                                className="pw-ms-task-status"
                                style={t.status.color ? { background: `${t.status.color}1f`, color: t.status.color } : undefined}
                              >
                                {t.status.label}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="pw-ms-task-priority">
                            {t.priority ? (
                              <span className={`pw-ms-priority pw-ms-priority-${String(t.priority).toLowerCase()}`}>{t.priority}</span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="pw-ms-task-due">
                            {formatDateOrDash(t.due_date)}
                            {overdue && <span className="pw-ms-task-overdue-tag">Overdue</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {pageCount > 1 && (
                <nav className="pw-ms-pagination" aria-label={`${milestone.name} task pages`}>
                  <span>
                    {(currentPage - 1) * TASK_PAGE_SIZE + 1}–{Math.min(currentPage * TASK_PAGE_SIZE, shownTasks.length)} of {shownTasks.length}
                  </span>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    aria-label="Previous task page"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
                  </Button>
                  <span aria-live="polite">Page {currentPage} of {pageCount}</span>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    aria-label="Next task page"
                    disabled={currentPage >= pageCount}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                  </Button>
                </nav>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// Compact overview above the list -- counts are the ACTIVE plan only
// (historical milestones never count). Each tile doubles as a quick status
// filter for the list below.
function MilestoneSummaryStrip({ counts, activeFilter, onFilter }) {
  return (
    <div className="pw-ms-summary" role="group" aria-label="Milestone summary">
      <button
        type="button"
        className={`pw-ms-summary-item pw-ms-summary-total${activeFilter === "" ? " is-active" : ""}`}
        aria-pressed={activeFilter === ""}
        onClick={() => onFilter("")}
      >
        <i className="fa-solid fa-flag-checkered" aria-hidden="true"></i>
        <strong>{counts.total}</strong>
        <span>Active Milestones</span>
      </button>
      {MILESTONE_CATEGORIES.map((c) => (
        <button
          key={c.key}
          type="button"
          className={`pw-ms-summary-item pw-ms-summary-${c.key}${activeFilter === c.key ? " is-active" : ""}`}
          aria-pressed={activeFilter === c.key}
          aria-label={`${counts[c.key]} ${c.label} milestones${c.key === "in_progress" && counts.ready ? `, ${counts.ready} ready to complete` : ""}`}
          onClick={() => onFilter(activeFilter === c.key ? "" : c.key)}
        >
          <i className={`fa-solid ${c.icon}`} aria-hidden="true"></i>
          <strong>{counts[c.key]}</strong>
          <span>{c.label}</span>
          {c.key === "in_progress" && counts.ready > 0 && (
            <em className="pw-ms-summary-note">{counts.ready} ready</em>
          )}
        </button>
      ))}
    </div>
  );
}

function MilestoneRow({ m, isExpanded, onToggle, onComplete, onEdit, onToggleActive, onDelete, children }) {
  const display = milestoneDisplayStatus(m);
  const summary = m.task_summary;
  const milestoneInactive = isMilestoneInactive(m);
  const phaseInactive = isPhaseInactive(m);
  const hasTasks = summary && summary.total > 0;
  const panelId = `pw-ms-panel-${m.id}`;

  return (
    <li
      className={`pw-ms-item${isExpanded ? " is-expanded" : ""}${milestoneInactive || phaseInactive ? " is-inactive" : ""}`}
      data-milestone-row={m.id}
    >
      <div className="pw-ms-row">
        <button
          type="button"
          className="pw-ms-toggle"
          aria-expanded={isExpanded}
          aria-controls={isExpanded ? panelId : undefined}
          aria-label={isExpanded ? `Hide tasks for ${m.name}` : `Show tasks for ${m.name}`}
          onClick={onToggle}
        >
          <i className={`fa-solid fa-chevron-right pw-ms-chevron${isExpanded ? " is-open" : ""}`} aria-hidden="true"></i>
          <span className="pw-ms-ident">
            <span className="pw-ms-name" title={m.name}>{m.name}</span>
            <span className="pw-ms-sub">
              <span className="pw-ms-phase" title={m.phase_name || "Unscoped"}>{m.phase_name || "Unscoped"}</span>
              <span className={`pw-ms-owner${m.owner_name ? "" : " is-empty"}`}>
                <i className="fa-regular fa-user" aria-hidden="true"></i> {m.owner_name || "Unassigned"}
              </span>
            </span>
          </span>
        </button>

        <div className="pw-ms-dates">
          <span><em>Planned</em> {formatDateOrNotScheduled(m.planned_date)}</span>
          <span><em>Actual</em> {formatDateOrDash(m.actual_date)}</span>
        </div>

        <div className="pw-ms-progress">
          {hasTasks ? (
            <>
              <SegmentedProgress summary={summary} label={`${m.name} task completion`} />
              <span className="pw-ms-progress-text">
                {allTasksCompleted(summary) ? (
                  <span className="pw-ms-all-done">
                    <i className="fa-solid fa-check" aria-hidden="true"></i> All Tasks Completed
                  </span>
                ) : (
                  <span>{summary.completed}/{summary.total} tasks</span>
                )}
                {summary.progress_percent !== null && summary.progress_percent !== undefined && (
                  <strong>{summary.progress_percent}%</strong>
                )}
              </span>
            </>
          ) : (
            <span className="pw-ms-progress-empty">No tasks linked</span>
          )}
        </div>

        <div className="pw-ms-state">
          <MilestoneStatusChip display={display} />
          {display.detail && <span className="pw-ms-state-detail">{display.detail}</span>}
          {(milestoneInactive || phaseInactive) && (
            <span className="pw-ms-state-detail">{milestoneInactive ? "Inactive" : "Phase inactive"}</span>
          )}
        </div>

        <div className="pw-ms-actions unit-actions">
          <Can moduleKey="project_milestones" actionKey="edit">
            {!m.actual_date && (
              <Button
                className="unit-icon-btn unit-complete"
                title="Complete Milestone"
                aria-label={`Mark ${m.name} complete`}
                onClick={onComplete}
              >
                <i className="fa-solid fa-check" aria-hidden="true"></i>
              </Button>
            )}
            <Button
              className="unit-icon-btn unit-edit"
              title="Edit Milestone"
              aria-label={`Edit ${m.name}`}
              onClick={onEdit}
            >
              <i className="fa-solid fa-pen" aria-hidden="true"></i>
            </Button>
            <Button
              className="unit-icon-btn unit-toggle"
              title={milestoneInactive ? "Activate Milestone" : "Pause Milestone"}
              aria-label={milestoneInactive ? `Activate ${m.name}` : `Deactivate ${m.name}`}
              onClick={onToggleActive}
            >
              <i className={`fa-solid ${milestoneInactive ? "fa-play" : "fa-pause"}`} aria-hidden="true"></i>
            </Button>
          </Can>
          <Can moduleKey="project_milestones" actionKey="delete">
            <Button
              className="unit-icon-btn unit-delete"
              title="Delete Milestone"
              aria-label={`Delete ${m.name}`}
              onClick={onDelete}
            >
              <i className="fa-solid fa-trash" aria-hidden="true"></i>
            </Button>
          </Can>
        </div>
      </div>
      {isExpanded && children}
    </li>
  );
}

const EMPTY_FILTERS = { search: "", status: "", phase: "", sort: "sequence" };

// Milestones tab -- self-fetching (GET /projects/:id/milestones), mirroring
// ProjectWorkspaceProcessTab.jsx's loading/error/self-fetch shape. Add/Edit
// is shown to anyone holding project_milestones:create/edit, but per the
// task spec a per-project Owner/Manager can also manage milestones without
// the global grant (server-enforced) -- rather than trying to reproduce
// that per-project check client-side, the buttons stay visible for such
// users too and an unauthorized attempt simply surfaces as a 403 toast,
// same convention as ProjectWorkspaceProcessTab's "Configure Process".
export default function ProjectWorkspaceMilestonesTab({ projectId }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const basePath = `/${user?.role || "admin"}`;

  const [milestones, setMilestones] = useState([]);
  const [phases, setPhases] = useState([]);
  const [modules, setModules] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Expand/collapse -- each milestone row expands/collapses independently
  // (a Set of expanded ids, not a single "active row"), and each row's task
  // data is fetched once and cached in `taskPanels` for the rest of this
  // page session -- see loadMilestoneTasks()'s effect below.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [taskPanels, setTaskPanels] = useState({});
  // Default view is the active plan only (active milestones on active
  // phases). Deactivated/historical milestones are kept in the database for
  // audit and can be brought back into view here.
  const [showInactive, setShowInactive] = useState(false);
  // List filters only narrow/reorder what the API already returned.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const setFilter = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    api.get(`/projects/${projectId}/phases`, { params: { is_active: 1, pageSize: 200 } })
      .then((res) => setPhases(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setPhases([]));
    api.get(`/projects/${projectId}/modules`, { params: { pageSize: 200 } })
      .then((res) => setModules(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setModules([]));
    api.get(`/projects/${projectId}/members`, { params: { pageSize: 200 } })
      .then((res) => setMembers(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setMembers([]));
  }, [projectId]);

  const load = () => {
    setLoading(true);
    setError("");
    api
      .get(`/projects/${projectId}/milestones`, { params: { is_active: showInactive ? "all" : 1 } })
      .then((res) => setMilestones(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((err) => setError(err.response?.data?.message || "Failed to load milestones"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId, showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeMilestones = useMemo(() => milestones.filter(isActivePlanMilestone), [milestones]);
  const historicalMilestones = useMemo(
    () => (showInactive ? milestones.filter((m) => !isActivePlanMilestone(m)) : []),
    [milestones, showInactive]
  );
  const counts = useMemo(() => summarizeMilestones(activeMilestones), [activeMilestones]);
  const phaseOptions = useMemo(() => {
    const names = [...new Set(milestones.map((m) => m.phase_name).filter(Boolean))];
    return { names, hasUnscoped: milestones.some((m) => !m.phase_name) };
  }, [milestones]);
  const shownActive = useMemo(() => filterAndSortMilestones(activeMilestones, filters), [activeMilestones, filters]);
  const shownHistorical = useMemo(() => filterAndSortMilestones(historicalMilestones, filters), [historicalMilestones, filters]);
  const filtersApplied = filters.search.trim() !== "" || filters.status !== "" || filters.phase !== "";

  const openAdd = () => { setEditing(null); setShowModal(true); };
  const openEdit = (m) => { setEditing(m); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditing(null); };
  const handleSaved = () => { closeModal(); load(); };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/projects/${projectId}/milestones/${m.id}/active-status`, { is_active: !m.is_active });
      toast.success(m.is_active ? "Milestone deactivated" : "Milestone activated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // Quick action -- the explicit sign-off. Sets actual_date to today and
  // submits through the same update flow the Edit modal uses (no separate
  // "complete" endpoint), so validation isn't bypassed and status/badge is
  // recomputed server-side. Never triggered automatically by task progress.
  const markComplete = async (m) => {
    try {
      await api.put(`/projects/${projectId}/milestones/${m.id}`, { actual_date: dayjs().format("YYYY-MM-DD") });
      toast.success("Milestone marked complete");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update milestone");
    }
  };

  // Loads (or force-refetches) one milestone's task panel. Never one request
  // per visible task -- a single GET returns the summary + every task row.
  const loadMilestoneTasks = (milestoneId, { force = false } = {}) => {
    if (!force && taskPanels[milestoneId]?.status === "ready") return;
    setTaskPanels((prev) => ({
      ...prev,
      [milestoneId]: { status: "loading", data: prev[milestoneId]?.data || null, error: "" },
    }));
    api
      .get(`/projects/${projectId}/milestones/${milestoneId}/tasks`)
      .then((res) => {
        setTaskPanels((prev) => ({ ...prev, [milestoneId]: { status: "ready", data: res.data?.data || null, error: "" } }));
      })
      .catch((err) => {
        setTaskPanels((prev) => ({
          ...prev,
          [milestoneId]: { status: "error", data: prev[milestoneId]?.data || null, error: err.response?.data?.message || "Could not load milestone tasks." },
        }));
      });
  };

  const toggleExpand = (milestoneId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(milestoneId)) {
        next.delete(milestoneId);
      } else {
        next.add(milestoneId);
      }
      return next;
    });
  };

  // Fetches a newly-expanded milestone's tasks exactly once -- collapsing
  // and re-expanding the same row within this page session reuses the
  // cached panel (loadMilestoneTasks()'s own `status === 'ready'` guard),
  // never refetching unless the Refresh button forces it.
  useEffect(() => {
    expandedIds.forEach((id) => {
      if (!taskPanels[id]) loadMilestoneTasks(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIds]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/projects/${projectId}/milestones/${deleteTarget.id}`);
      toast.success("Milestone deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete milestone");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState message="Loading milestones…" />;
  if (error) return <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />;

  const renderRow = (m) => (
    <MilestoneRow
      key={m.id}
      m={m}
      isExpanded={expandedIds.has(m.id)}
      onToggle={() => toggleExpand(m.id)}
      onComplete={() => markComplete(m)}
      onEdit={() => openEdit(m)}
      onToggleActive={() => toggleActive(m)}
      onDelete={() => setDeleteTarget(m)}
    >
      <MilestoneTasksPanel
        milestone={m}
        panelState={taskPanels[m.id]}
        onRetry={() => loadMilestoneTasks(m.id, { force: true })}
        onRefresh={() => loadMilestoneTasks(m.id, { force: true })}
        onTaskClick={(taskId) => navigate(`${basePath}/tasks/${taskId}`)}
        currentUserId={user?.id}
      />
    </MilestoneRow>
  );

  return (
    <div className="pw-ms">
      <div className="pw-ms-header">
        <div>
          <h5 className="pw-ms-heading">Milestones</h5>
          <p className="pw-ms-subheading">Dependency checkpoints for the active project plan</p>
        </div>
        <div className="pw-ms-header-actions">
          <Form.Check
            type="switch"
            id={`pw-milestones-show-inactive-${projectId}`}
            label="Show inactive (historical) milestones"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <Can moduleKey="project_milestones" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={openAdd}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i> Add Milestone
            </Button>
          </Can>
        </div>
      </div>

      {milestones.length === 0 ? (
        <EmptyState
          title="No milestones yet"
          message="Add milestones to track key deliverables for this project."
        />
      ) : (
        <>
          <MilestoneSummaryStrip counts={counts} activeFilter={filters.status} onFilter={(v) => setFilter("status", v)} />

          <div className="pw-ms-toolbar">
            <div className="pw-ms-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              <Form.Control
                size="sm"
                type="search"
                placeholder="Search milestones, phases or owners"
                aria-label="Search milestones"
                value={filters.search}
                onChange={(e) => setFilter("search", e.target.value)}
              />
            </div>
            <Form.Select
              size="sm"
              aria-label="Filter milestones by status"
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="ready">Ready to Complete</option>
              <option value="upcoming">Upcoming</option>
              <option value="overdue">Overdue</option>
            </Form.Select>
            <Form.Select
              size="sm"
              aria-label="Filter milestones by phase"
              value={filters.phase}
              onChange={(e) => setFilter("phase", e.target.value)}
            >
              <option value="">All phases</option>
              {phaseOptions.names.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
              {phaseOptions.hasUnscoped && <option value={UNSCOPED_PHASE}>Unscoped</option>}
            </Form.Select>
            <Form.Select
              size="sm"
              aria-label="Sort milestones"
              value={filters.sort}
              onChange={(e) => setFilter("sort", e.target.value)}
            >
              <option value="sequence">Sequence</option>
              <option value="planned_asc">Planned date (earliest)</option>
              <option value="planned_desc">Planned date (latest)</option>
              <option value="progress_desc">Progress (highest)</option>
              <option value="progress_asc">Progress (lowest)</option>
            </Form.Select>
            {filtersApplied && (
              <Button size="sm" variant="link" className="pw-ms-clear" onClick={() => setFilters((prev) => ({ ...EMPTY_FILTERS, sort: prev.sort }))}>
                Clear filters
              </Button>
            )}
            <span className="pw-ms-count" aria-live="polite">
              {shownActive.length} of {activeMilestones.length} active
            </span>
          </div>

          {shownActive.length > 0 ? (
            <ul className="pw-ms-list" aria-label="Active milestones">{shownActive.map(renderRow)}</ul>
          ) : (
            <p className="pw-ms-empty">
              {activeMilestones.length === 0 ? "No active milestones." : "No milestones match the current filters."}
            </p>
          )}

          {showInactive && (
            <section className="pw-ms-historical" aria-label="Historical milestones">
              <h6 className="pw-ms-historical-title">
                <i className="fa-solid fa-box-archive" aria-hidden="true"></i> Historical milestones
                <span className="pw-ms-historical-count">{shownHistorical.length}</span>
              </h6>
              <p className="pw-ms-historical-note">
                Inactive milestones, or milestones on inactive phases. Kept for audit; not counted in the summary above.
              </p>
              {shownHistorical.length > 0 ? (
                <ul className="pw-ms-list">{shownHistorical.map(renderRow)}</ul>
              ) : (
                <p className="pw-ms-empty">
                  {historicalMilestones.length === 0 ? "No historical milestones." : "No historical milestones match the current filters."}
                </p>
              )}
            </section>
          )}
        </>
      )}

      <MilestoneFormModal
        show={showModal}
        projectId={projectId}
        phases={phases}
        modules={modules}
        members={members}
        editing={editing}
        onClose={closeModal}
        onSaved={handleSaved}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        busy={deleting}
        title="Delete Milestone?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
      />
    </div>
  );
}
